import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { OrganizationService } from '../../services/organizationService';
import { logger } from '../../services/loggingService';
import { supabase } from '../../lib/supabase';
import { ConfirmModal } from '../Common/ConfirmModal';
import { Building2, Check, X, Loader2, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';

export function AcceptInvitationPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [invitation, setInvitation] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [declineConfirm, setDeclineConfirm] = useState<{
    isOpen: boolean;
    loading: boolean;
  }>({
    isOpen: false,
    loading: false,
  });

  useEffect(() => {
    loadInvitation();
  }, []);

  const loadInvitation = async () => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');

    if (!token) {
      setError('Invalid invitation link');
      setLoading(false);
      return;
    }

    try {
      // Fetch invitation details
      const { data, error: fetchError } = await supabase
        .from('organization_invitations')
        .select(`
          *,
          organization:organization_id (
            name,
            description
          )
        `)
        .eq('token', token)
        .eq('status', 'pending')
        .single();

      if (fetchError || !data) {
        setError('Invitation not found or has expired');
        return;
      }

      // Check if expired
      if (new Date(data.expires_at) < new Date()) {
        setError('This invitation has expired');
        return;
      }

      setInvitation(data);
    } catch (err) {
      logger.error('Error loading invitation', { error: err, token });
      setError('Failed to load invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!user || !invitation) return;

    setAccepting(true);
    try {
      const params = new URLSearchParams(window.location.search);
      const token = params.get('token');

      if (!token) {
        toast.error('Invalid invitation');
        return;
      }

      const { success, error } = await OrganizationService.acceptInvitation(token, user.id);

      if (error) {
        toast.error(error);
        return;
      }

      if (success) {
        toast.success('Welcome to the team! 🎉');
        // Redirect to dashboard
        window.location.href = '/';
      }
    } catch (err) {
      logger.error('Error accepting invitation', { error: err, userId: user?.id, token });
      toast.error('Failed to accept invitation');
    } finally {
      setAccepting(false);
    }
  };

  const handleDecline = () => {
    if (!invitation) return;
    setDeclineConfirm({ isOpen: true, loading: false });
  };

  const confirmDecline = async () => {
    if (!invitation) return;

    setDeclineConfirm(prev => ({ ...prev, loading: true }));

    try {
      const params = new URLSearchParams(window.location.search);
      const token = params.get('token');

      if (!token) {
        setDeclineConfirm({ isOpen: false, loading: false });
        return;
      }

      await supabase
        .from('organization_invitations')
        .update({ status: 'declined' })
        .eq('token', token);

      toast.success('Invitation declined');
      window.location.href = '/';
    } catch (err) {
      logger.error('Error declining organization invitation', { error: err, token });
      toast.error('Failed to decline invitation');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-accent-500 mx-auto mb-4" />
          <p className="text-secondary">Loading invitation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-base flex items-center justify-center p-4">
        <div className="max-w-md w-full card-base rounded-2xl shadow-xl p-8 text-center">
          <div className="w-16 h-16 bg-error-light rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-error" />
          </div>
          <h2 className="text-2xl font-bold text-primary mb-2">Invalid Invitation</h2>
          <p className="text-secondary mb-6">{error}</p>
          <button
            onClick={() => (window.location.href = '/')}
            className="btn btn-primary"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-base flex items-center justify-center p-4">
        <div className="max-w-md w-full card-base rounded-2xl shadow-xl p-8 text-center">
          <div className="w-16 h-16 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Building2 className="w-8 h-8 text-accent-500" />
          </div>
          <h2 className="text-2xl font-bold text-primary mb-2">Sign In Required</h2>
          <p className="text-secondary mb-6">
            Please sign in to accept this invitation to join {invitation?.organization?.name}
          </p>
          <button
            onClick={() => (window.location.href = '/cy-admin')}
            className="btn btn-primary w-full"
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-base flex items-center justify-center p-4">
      <div className="max-w-2xl w-full card-base rounded-2xl shadow-xl overflow-hidden">
        {/* Header */}
        <div className="bg-accent-500 p-8 text-center">
          <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Building2 className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">You're Invited!</h1>
          <p className="text-white/90">Join your team on Cyclone</p>
        </div>

        {/* Content */}
        <div className="p-8">
          {/* Organization Info */}
          <div className="bg-subtle rounded-xl p-6 mb-6">
            <h2 className="text-xl font-semibold text-primary mb-2">
              {invitation?.organization?.name}
            </h2>
            {invitation?.organization?.description && (
              <p className="text-secondary mb-4">{invitation.organization.description}</p>
            )}
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-muted">Role:</span>
                <span className="badge badge-primary">
                  {invitation?.role?.charAt(0).toUpperCase() + invitation?.role?.slice(1)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-muted">Invited to:</span>
                <span className="font-medium text-primary">{invitation?.email}</span>
              </div>
            </div>
          </div>

          {/* What You'll Get */}
          <div className="mb-8">
            <h3 className="font-semibold text-primary mb-4">What you'll get access to:</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                <span className="text-primary">
                  Collaborate on projects with your team
                </span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                <span className="text-primary">
                  Access to AI agents and automation tools
                </span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                <span className="text-primary">
                  Shared prompts, blueprints, and resources
                </span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                <span className="text-primary">
                  Real-time collaboration and updates
                </span>
              </li>
            </ul>
          </div>

          {/* Expiry Warning */}
          <div className="alert alert-warning mb-6">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <p className="text-sm">
              This invitation expires on{' '}
              <strong>{new Date(invitation?.expires_at).toLocaleDateString()}</strong>
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-4">
            <button
              onClick={handleDecline}
              disabled={accepting}
              className="btn btn-secondary flex-1 disabled:opacity-50"
            >
              <X className="w-5 h-5 inline mr-2" />
              Decline
            </button>
            <button
              onClick={handleAccept}
              disabled={accepting}
              className="btn btn-primary flex-1 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {accepting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Accepting...
                </>
              ) : (
                <>
                  <Check className="w-5 h-5" />
                  Accept Invitation
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={declineConfirm.isOpen}
        onClose={() => setDeclineConfirm({ isOpen: false, loading: false })}
        onConfirm={confirmDecline}
        title="Decline Invitation"
        message="Are you sure you want to decline this invitation?"
        confirmText="Decline"
        cancelText="Cancel"
        confirmVariant="danger"
        loading={declineConfirm.loading}
      />
    </div>
  );
}
